import React, { useEffect, useState, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import UploadQuestions from "./UploadQuestions";
import Notification from "./Notification";

import {
  fetchExams,
  fetchExamQuestions,
  updateExamQuestion,
  deleteExamQuestion,
  fetchPresetsByExam,
  updatePresetName,
  addCoursesToPreset,
  removeAllCoursesFromPreset,
} from "../utils/api";

import styles from "./ManageExams.module.css";

const MAX_PRESETS = 8;

// small util
const truncate = (text, n = 90) => {
  if (!text) return "";
  const s = String(text);
  return s.length > n ? s.slice(0, n) + "…" : s;
};

const ManageExams = () => {
  const [examList, setExamList] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState("");

  const [examData, setExamData] = useState([]);
  const [typeHQuestions, setTypeHQuestions] = useState([]);

  // Presets / matrix
  const [presets, setPresets] = useState([]);
  const [presetNames, setPresetNames] = useState({});
  const [matrixMembership, setMatrixMembership] = useState({});

  // UI state
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [showMatrix, setShowMatrix] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showIds, setShowIds] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const token = localStorage.getItem("token");

  // Load exams once
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchExams(token);
        setExamList(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setNotification({ message: "Failed to load exams.", type: "error" });
      }
    })();
  }, [token]);

  // Load questions for selected exam
  const loadExamQuestions = useCallback(async () => {
    if (!selectedExamId) {
      setExamData([]);
      setTypeHQuestions([]);
      return;
    }
    try {
      const data = await fetchExamQuestions(selectedExamId, null, token);
      const arr = Array.isArray(data) ? data : [];
      setExamData(arr);
      setTypeHQuestions(arr.filter((q) => q.type === "H"));
    } catch (err) {
      console.error(err);
      setNotification({ message: "Failed to load questions.", type: "error" });
    }
  }, [selectedExamId, token]);

  useEffect(() => {
    loadExamQuestions();
  }, [selectedExamId, loadExamQuestions]);

  // ---- Presets / Matrix ----
  const initMatrixMembership = (presetsArr) => {
    const membership = {};
    presetsArr.forEach((p) => {
      (p.courses || []).forEach((c) => {
        membership[`${p.preset_id}-${c.course_id}`] = true;
      });
    });
    setMatrixMembership(membership);
  };

  const loadPresets = useCallback(async () => {
    if (!selectedExamId) {
      setPresets([]);
      setPresetNames({});
      return;
    }
    try {
      const data = await fetchPresetsByExam(selectedExamId, token);
      const arr = (Array.isArray(data) ? data : [])
        .sort((a, b) => a.preset_id - b.preset_id)
        .slice(0, MAX_PRESETS);

      setPresets(arr);

      const names = arr.reduce((acc, p) => {
        acc[p.preset_id] = p.preset_name || "SPARE";
        return acc;
      }, {});
      setPresetNames(names);

      initMatrixMembership(arr);
    } catch (err) {
      console.error(err);
      setNotification({ message: "Failed to load presets.", type: "error" });
    }
  }, [selectedExamId, token]);

  useEffect(() => {
    loadPresets();
  }, [selectedExamId, loadPresets]);

  const handleToggleCheckbox = (pid, cid) => {
    const key = `${pid}-${cid}`;
    setMatrixMembership((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleLocalPresetNameChange = (pid, val) => {
    setPresetNames((prev) => ({ ...prev, [pid]: val }));
  };

  const selectAllInPreset = (pid, value) => {
    setMatrixMembership((prev) => {
      const next = { ...prev };
      (typeHQuestions || []).forEach((q) => {
        next[`${pid}-${q.course_id}`] = value;
      });
      return next;
    });
  };

  const saveMatrixChanges = async () => {
    if (!presets.length) return;
    try {
      // reset all preset-course links, then re-add checked
      for (const p of presets) {
        await removeAllCoursesFromPreset(p.preset_id, token);
      }
      for (const [key, checked] of Object.entries(matrixMembership)) {
        if (!checked) continue;
        const [pid, cid] = key.split("-");
        await addCoursesToPreset(pid, [cid], token);
      }
      // update names if changed
      for (const p of presets) {
        const local = presetNames[p.preset_id] || "SPARE";
        if (local !== p.preset_name) {
          await updatePresetName(p.preset_id, local, token);
        }
      }
      await loadPresets();
      setNotification({ message: "Preset saved!", type: "success" });
    } catch (err) {
      console.error(err);
      setNotification({ message: "Failed to save presets.", type: "error" });
    }
  };

  // ---- Table actions ----
  const handleDeleteQuestion = async (qid) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await deleteExamQuestion(qid, token);
      setExamData((prev) => prev.filter((q) => q.id !== qid));
      setTypeHQuestions((prev) => prev.filter((q) => q.id !== qid));
      setNotification({ message: "Deleted!", type: "success" });
    } catch (err) {
      console.error(err);
      setNotification({ message: "Failed to delete.", type: "error" });
    }
  };

  const handleDownloadExam = () => {
    if (!selectedExamId) return;
    const ex = examList.find((e) => e.id === +selectedExamId) || {};
    const name = ex.exam_name || `exam-${selectedExamId}`;
    try {
      const data = examData.map(({ id, ...rest }) => rest);
      const sheet = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, sheet, "ExamData");
      XLSX.writeFile(wb, `${name}.xlsx`);
    } catch (err) {
      console.error(err);
      setNotification({ message: "Failed to generate file.", type: "error" });
    }
  };

  // ---- Filtering ----
  const filteredExamData = useMemo(() => {
    if (!searchTerm.trim()) return examData;
    const t = searchTerm.toLowerCase();
    return examData.filter((row) =>
      [
        row.type,
        row.question,
        row.explanation,
        row.course_id,
        row.right_ans,
        row.ans1,
        row.ans2,
        row.ans3,
        row.ans4,
      ]
        .map((x) => (x == null ? "" : String(x)))
        .some((val) => val.toLowerCase().includes(t))
    );
  }, [examData, searchTerm]);

  // ---- Modal ----
  const openModal = (row) => {
    setSelectedRow(row);
    setShowDetailModal(true);
  };
  const closeModal = () => {
    setShowDetailModal(false);
    setSelectedRow(null);
  };
  const updateSelected = (key, val) =>
    setSelectedRow((prev) => ({ ...prev, [key]: val }));

  const saveModalChanges = async () => {
    if (!selectedRow) return;
    try {
      await updateExamQuestion(selectedRow.id, selectedRow, token);

      // reflect in UI
      setExamData((prev) =>
        prev.map((r) => (r.id === selectedRow.id ? { ...selectedRow } : r))
      );

      setNotification({ message: "Question saved!", type: "success" });
      closeModal();
    } catch (err) {
      console.error(err);
      setNotification({ message: "Failed to save.", type: "error" });
    }
  };

  return (
    <div className={styles.container}>
      {notification.message && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification({ message: "", type: "" })}
        />
      )}

      {/* Controls */}
      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <h3 className={styles.sectionTitle}>Manage Exams</h3>
          {selectedExamId && (
            <div className={styles.matrixToggle}>
              <button
                className={styles.ghostBtn}
                onClick={() => setShowMatrix(!showMatrix)}
              >
                {showMatrix ? "Hide Preset Matrix" : "Show Preset Matrix"}
              </button>
            </div>
          )}
        </div>

        <div className={styles.controlsRow}>
          <div className={styles.selectorGroup}>
            <label className={styles.label}>Select Exam:</label>
            <select
              value={selectedExamId}
              onChange={(e) => {
                setSelectedExamId(e.target.value);
                setShowMatrix(false);
                setSearchTerm("");
              }}
              className={styles.select}
            >
              <option value="">-- Choose an exam --</option>
              {examList.map((exam) => (
                <option key={exam.id} value={exam.id}>
                  {exam.exam_name}
                </option>
              ))}
            </select>
          </div>

          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={showIds}
              onChange={(e) => setShowIds(e.target.checked)}
              disabled={!selectedExamId}
            />
            Show IDs
          </label>

          <div className={styles.searchWrap}>
            <input
              className={styles.searchInput}
              placeholder="Search questions, answers"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={!selectedExamId}
            />
            {selectedExamId ? (
              <span className={styles.countBadge}>
                {filteredExamData.length}/{examData.length}
              </span>
            ) : null}
          </div>

          <div className={styles.inlineControls}>
            <button
              onClick={() => setShowUploadModal(true)}
              className={styles.secondaryBtn}
            >
              Upload Exam
            </button>
            <button
              onClick={handleDownloadExam}
              className={styles.secondaryBtn}
              disabled={!selectedExamId}
            >
              Download Exam
            </button>
          </div>
        </div>
      </section>

      {/* Preset Matrix */}
      {showMatrix && selectedExamId && typeHQuestions.length > 0 && (
        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <h3 className={styles.sectionTitle}>
              Presets for Type = "H" Questions
            </h3>
            <div className={styles.inlineControls}>
              <button onClick={saveMatrixChanges} className={styles.primaryBtn}>
                Save Matrix Changes
              </button>
            </div>
          </div>

          <div className={styles.tableWrapper}>
            <table className={styles.matrixTable}>
              <thead>
                <tr>
                  <th>Question</th>
                  {presets.map((p) => (
                    <th key={p.preset_id}>
                      <div className={styles.presetHeader}>
                        <input
                          className={styles.presetNameInput}
                          type="text"
                          value={presetNames[p.preset_id]}
                          onChange={(e) =>
                            handleLocalPresetNameChange(
                              p.preset_id,
                              e.target.value
                            )
                          }
                        />
                        <div className={styles.presetBulkBtns}>
                          <button
                            type="button"
                            className={styles.microBtn}
                            title="Select all"
                            onClick={() => selectAllInPreset(p.preset_id, true)}
                          >
                            All
                          </button>
                          <button
                            type="button"
                            className={styles.microBtn}
                            title="Clear all"
                            onClick={() =>
                              selectAllInPreset(p.preset_id, false)
                            }
                          >
                            None
                          </button>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {typeHQuestions.map((q) => (
                  <tr key={q.id}>
                    <td className={styles.matrixQuestionCell}>{q.question}</td>
                    {presets.map((p) => {
                      const key = `${p.preset_id}-${q.course_id}`;
                      return (
                        <td key={key} className={styles.matrixCell}>
                          <input
                            type="checkbox"
                            checked={!!matrixMembership[key]}
                            onChange={() =>
                              handleToggleCheckbox(p.preset_id, q.course_id)
                            }
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Compact Questions List */}
      {selectedExamId && !showMatrix && (
        <section className={styles.card}>
          {examData.length === 0 ? (
            <p className={styles.muted}>
              No questions available for this exam.
            </p>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={`${styles.examTable} ${styles.compactTable}`}>
                <thead>
                  <tr>
                    {showIds && <th className={styles.colMiniId}>ID</th>}
                    <th className={styles.colTypeMini}>Type</th>
                    <th className={styles.colQuestionMini}>Question</th>
                    <th className={styles.colCourseMini}>Course ID</th>
                    <th className={styles.colActionsMini}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExamData.map((row) => (
                    <tr key={row.id}>
                      {showIds && (
                        <td className={styles.nowrap}>{row.id ?? "N/A"}</td>
                      )}
                      <td className={styles.nowrap}>{row.type || ""}</td>
                      <td title={row.question || ""}>
                        {truncate(row.question, 120)}
                      </td>
                      <td className={styles.nowrap}>{row.course_id || ""}</td>
                      <td className={styles.actionsCell}>
                        <button
                          className={styles.viewBtn}
                          onClick={() => openModal(row)}
                        >
                          View / Edit
                        </button>
                        <button
                          className={styles.deleteBtn}
                          onClick={() => handleDeleteQuestion(row.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRow && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3>Edit Question {showIds ? `#${selectedRow.id}` : ""}</h3>
            </div>

            <div className={styles.modalForm}>
              <div className={styles.formRow}>
                <label>Type</label>
                <input
                  className={styles.shortInput}
                  value={selectedRow.type || ""}
                  onChange={(e) => updateSelected("type", e.target.value)}
                />
              </div>

              <div className={styles.formRow}>
                <label>Course ID</label>
                <input
                  className={styles.shortInput}
                  value={selectedRow.course_id || ""}
                  onChange={(e) => updateSelected("course_id", e.target.value)}
                />
              </div>

              <div className={styles.formRow}>
                <label>Question</label>
                <textarea
                  className={styles.textarea}
                  rows={4}
                  value={selectedRow.question || ""}
                  onChange={(e) => updateSelected("question", e.target.value)}
                />
              </div>

              <div className={styles.formRow}>
                <label>Explanation</label>
                <textarea
                  className={styles.textarea}
                  rows={4}
                  value={selectedRow.explanation || ""}
                  onChange={(e) =>
                    updateSelected("explanation", e.target.value)
                  }
                />
              </div>

              <div className={styles.formRow}>
                <label>Right Answer</label>
                <select
                  className={styles.rightAnsSelect}
                  value={selectedRow.right_ans ?? ""}
                  onChange={(e) => updateSelected("right_ans", e.target.value)}
                >
                  <option value="">—</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                </select>
              </div>

              {["ans1", "ans2", "ans3", "ans4"].map((col, i) => (
                <div className={styles.formRow} key={col}>
                  <label>{`Option ${i + 1}`}</label>
                  <textarea
                    className={`${styles.textarea} ${
                      String(selectedRow.right_ans) === String(i + 1)
                        ? styles.correctOption
                        : ""
                    }`}
                    rows={3}
                    value={selectedRow[col] || ""}
                    onChange={(e) => updateSelected(col, e.target.value)}
                  />
                </div>
              ))}
            </div>

            <div className={styles.modalActions}>
              <button className={styles.primaryBtn} onClick={saveModalChanges}>
                Save
              </button>
              <button className={styles.ghostBtn} onClick={closeModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <UploadQuestions
        showModal={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        examList={examList}
      />
    </div>
  );
};

export default ManageExams;
